// Sistema de detección de "nueva versión disponible".
//
// Cómo funciona:
//   1. Al montar Play, fetch /static/version.json y guardar el `buildId`
//      como referencia (versión que cargó el user).
//   2. Cada 5 min Y al recuperar foco del tab (visibilitychange visible),
//      re-fetch y comparar con la referencia.
//   3. Si cambió, llamar el callback registrado — el cliente abre un modal
//      "Recargar ahora / Después".
//
// El user que clickea "Después" entra en snooze de 5 min — no vuelve a
// notificar hasta que pase ese tiempo. Si el user no abre nunca el modal,
// el snooze persiste pero la versión sigue siendo nueva (no se "olvida").
//
// El server.js escribe version.json en cada boot del container cliente.
// docker compose restart cliente → new buildId → user verá el modal.

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const SNOOZE_MS = 5 * 60 * 1000;         // 5 min después de "Después"
const URL = "/static/version.json";

class VersionWatch {
    constructor() {
        this.initialBuildId = null;
        this.lastSeenBuildId = null;
        this.snoozedUntil = 0;
        this.pollInterval = null;
        this.onNewVersion = null; // callback (newBuildId)
        this._visHandler = null;
        this._active = false;
    }

    // Llamado por play/index.js componentDidMount. cb se invoca cuando se
    // detecta una versión nueva (post-snooze check).
    async start(cb) {
        if (this._active) return;
        this._active = true;
        this.onNewVersion = cb;
        const initial = await this._fetchBuildId();
        if (initial) {
            this.initialBuildId = initial;
            this.lastSeenBuildId = initial;
        }
        this.pollInterval = setInterval(() => this._check(), POLL_INTERVAL_MS);
        if (typeof document !== "undefined") {
            this._visHandler = () => {
                if (!document.hidden) this._check();
            };
            document.addEventListener("visibilitychange", this._visHandler);
        }
    }

    stop() {
        this._active = false;
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        if (this._visHandler) {
            document.removeEventListener("visibilitychange", this._visHandler);
            this._visHandler = null;
        }
    }

    // User clickeó "Después" — silenciamos por SNOOZE_MS.
    snooze() {
        this.snoozedUntil = Date.now() + SNOOZE_MS;
    }

    async _fetchBuildId() {
        try {
            // cache-buster query → siempre pega al network (sino el browser
            // podría devolver el version.json viejo cacheado).
            const resp = await fetch(URL + "?t=" + Date.now(), {
                cache: "no-store"
            });
            if (!resp.ok) return null;
            const data = await resp.json();
            return data && data.buildId ? String(data.buildId) : null;
        } catch (e) {
            return null;
        }
    }

    async _check() {
        if (!this._active || !this.initialBuildId) return;
        if (Date.now() < this.snoozedUntil) return;
        const current = await this._fetchBuildId();
        if (!current) return;
        this.lastSeenBuildId = current;
        if (current !== this.initialBuildId) {
            if (this.onNewVersion) {
                try {
                    this.onNewVersion(current);
                } catch (e) {}
            }
        }
    }
}

const versionWatch = new VersionWatch();

if (typeof window !== "undefined") {
    window._versionWatch = versionWatch; // debug
}

export default versionWatch;
